// Per-chapter editor registry
const _editors = {};

function initializeChapterEditor(elementId, sectionsJson, dotNetHelper, headerHtml, footerHtml) {
    const el = document.getElementById(elementId);
    if (!el) {
        console.error('TipTap ChapterEditor: element not found', elementId);
        return;
    }
    if (_editors[elementId]) {
        _editors[elementId].reactRoot.unmount();
        delete _editors[elementId];
    }

    // Wrap each section's HTML in a <section data-section-id="..."> element so
    // SectionNode's parseHTML picks up both the content and the DB id.
    let htmlContent = '';
    let firstSectionId = null;
    if (sectionsJson && sectionsJson.length > 0) {
        firstSectionId = sectionsJson[0].sectionId ?? null;
        htmlContent = sectionsJson.map(s => {
            const idAttr = s.sectionId ? ` data-section-id="${s.sectionId}"` : '';
            return `<section${idAttr}>${s.html || '<p></p>'}</section>`;
        }).join('');
    }
    if (!htmlContent) htmlContent = '<section><p></p></section>';

    const canvasBg = getComputedStyle(document.documentElement)
        .getPropertyValue('--mud-palette-background').trim() || '#f4f4f5';

    const TT = window.TipTap;
    const extensions = [
        TT.Pages.configure({
            pageFormat: 'A4',
            pageGap: 30,
            pageGapBackground: canvasBg,
            header: headerHtml || '',
            footer: footerHtml || 'Page {page} of {total}',
        }),
        TT.ConvertKit.configure({
            table: false,
            image: false,
            history: false,  // UndoRedo extension handles history
            heading: { levels: [1, 2, 3, 4] },
            link: { openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } },
        }),
        TT.TableKit,
        TT.Image.configure({
            inline: false,
            allowBase64: true,
        }),
        TT.SectionNode,
        TT.UniqueId,
        TT.UndoRedo,
    ];

    const entry = {
        editor: null,
        reactRoot: null,
        firstSectionId,
        headerHtml: headerHtml || '',
        footerHtml: footerHtml || '',
    };
    _editors[elementId] = entry;

    const { React, ReactDOM, ChapterEditorApp } = TT;
    const root = ReactDOM.createRoot(el);
    let autosaveTimer = null;
    let headerObserver = null;

    root.render(
        React.createElement(ChapterEditorApp, {
            extensions,
            content: htmlContent,
            mode: 'chapter',
            dotNetHelper,
            onEditorReady: (editor) => {
                entry.editor = editor;

                editor.on('update', () => {
                    clearTimeout(autosaveTimer);
                    autosaveTimer = setTimeout(() => {
                        dotNetHelper.invokeMethodAsync('OnEditorUpdate').catch(() => {});
                    }, 1500);
                });

                const proseMirrorEl = editor.view.dom;
                let rafId = null;

                const applyHeaderOffsets = () => {
                    if (rafId) cancelAnimationFrame(rafId);
                    rafId = requestAnimationFrame(() => {
                        proseMirrorEl.querySelectorAll('[data-header-offset]').forEach(el => {
                            el.style.marginTop = '';
                            el.removeAttribute('data-header-offset');
                        });
                        proseMirrorEl.querySelectorAll('.ProseMirror-widget').forEach(widget => {
                            const header = widget.querySelector('.tiptap-page-header');
                            if (!header) return;
                            const h = header.getBoundingClientRect().height;
                            if (!h) return;
                            let sibling = widget.nextElementSibling;
                            while (sibling && sibling.classList.contains('ProseMirror-widget')) {
                                sibling = sibling.nextElementSibling;
                            }
                            if (!sibling) return;
                            sibling.style.marginTop = `${h}px`;
                            sibling.setAttribute('data-header-offset', 'true');
                        });
                    });
                };

                applyHeaderOffsets();
                headerObserver = new MutationObserver(applyHeaderOffsets);
                headerObserver.observe(proseMirrorEl, { childList: true, subtree: true });
            },
        })
    );

    entry.reactRoot = root;
    entry.headerObserver = headerObserver;
}

function getChapterEditorSections(elementId) {
    const entry = _editors[elementId];
    if (!entry || !entry.editor) return [];

    // editor.getHTML() uses schema serialization (renderHTML), which outputs
    // <section class="editor-section" data-section-id="...">...</section>.
    const fullHtml = entry.editor.getHTML();
    const parsed = new DOMParser().parseFromString(fullHtml, 'text/html');
    const sectionEls = parsed.querySelectorAll('section.editor-section');

    if (sectionEls.length === 0) {
        // Fallback: no sections found, return whole content as one record.
        return [{ sectionId: entry.firstSectionId, html: fullHtml }];
    }

    return Array.from(sectionEls).map((el, i) => ({
        sectionId: el.getAttribute('data-section-id') || (i === 0 ? entry.firstSectionId : null),
        html: el.innerHTML,
    }));
}

function getChapterEditorHeaderFooter(elementId) {
    const entry = _editors[elementId];
    if (!entry || !entry.editor) return { headerHtml: '', footerHtml: '' };
    const storage = entry.editor.storage.pages;
    return {
        headerHtml: (storage && storage.headerHTML) || entry.headerHtml || '',
        footerHtml: (storage && storage.footerHTML) || entry.footerHtml || '',
    };
}

function destroyTipTap(elementId) {
    const entry = _editors[elementId];
    if (entry) {
        if (entry.headerObserver) entry.headerObserver.disconnect();
        if (entry.reactRoot) entry.reactRoot.unmount();
        delete _editors[elementId];
    }
}

//# sourceMappingURL=tiptapIntegration.js.map
